import React, { useState, useCallback, memo, useRef, useEffect } from "react";
import {
  Paper,
  Box,
  Typography,
  Grid,
  Input,
  FormControlLabel,
  Checkbox,
  ThemeProvider,
  createTheme,
  Button,
  CircularProgress,
} from "@mui/material";
import html2canvas from "html2canvas";
// @ts-ignore
import html2pdf from "html2pdf.js";
import { Download, Image } from "@mui/icons-material";
import PersonIcon from "@mui/icons-material/Person";
import {
  convertDateToHindiWords,
  formatDateFromForm,
} from "../../utils/dateToWordsHindi";
import { useStudentPhoto, useStudentSignature } from "../../hooks/useStudents";
import { useSubjectsByClassId } from "../../hooks/useSubjects";
import { useSchools } from "../../hooks/useSchools";
import { useAuth } from "../../contexts/AuthContext";
import { useSchoolIdForAdmin } from "../../hooks/useSchoolIdForAdmin";

const theme = createTheme({
  typography: {
    fontFamily: `"Noto Sans Devanagari","Nirmala UI","Mangal","Hind","Arial",sans-serif`,
  },
});

const PrintText: React.FC<{ children: React.ReactNode; sx?: any }> = ({
  children,
  sx,
}) => (
  <Box
    sx={{
      fontFamily: `"Noto Sans Devanagari","Nirmala UI","Mangal","Hind","Arial",sans-serif`,
      fontFeatureSettings: "'liga' 1, 'clig' 1, 'kern' 1",
      WebkitFontSmoothing: "antialiased",
      whiteSpace: "pre-wrap",
      lineHeight: 1.2,
      ...sx,
    }}
  >
    {children}
  </Box>
);

interface LabelProps {
  children: React.ReactNode;
  sx?: any;
}

interface RowProps {
  label: React.ReactNode;
  labelWidth?: string;
}

const Row: React.FC<RowProps> = memo(({ label, labelWidth = "130mm" }) => (
  <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
    <Box sx={{ width: labelWidth, flexShrink: 0 }}>
      <Label sx={{ fontSize: "13px", fontWeight: "bold" }}>{label}</Label>
    </Box>
  </Box>
));

const Label: React.FC<LabelProps> = memo(({ children, sx = {} }) => (
  <Typography
    sx={{
      fontSize: "13px",
      fontWeight: "bold",
      lineHeight: 1,
      ...sx,
    }}
  >
    {children}
  </Typography>
));

interface CellProps {
  width?: string;
  height?: string;
  children?: React.ReactNode;
  sx?: any;
}

const Cell: React.FC<CellProps> = memo(
  ({ width = "6mm", height = "6mm", children, sx = {} }) => (
    <Box
      sx={{
        width,
        height,
        border: "1px solid #000",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "10px",
        fontWeight: "bold",
        ...sx,
      }}
    >
      {children}
    </Box>
  )
);

interface CharBoxesProps {
  count: number;
  value: string;
  onChange?: (value: string) => void;
  boxSizeMm?: number;
  gapMm?: number;
  printMode?: boolean;
}

const CharBoxes: React.FC<CharBoxesProps> = memo(
  ({ count, value, onChange, boxSizeMm = 6, gapMm = 0, printMode = false }) => {
    const sanitizeInput = useCallback((input: string) => {
      return input
        .toUpperCase()
        .replace(/[^A-Z0-9\s.\-@]/g, "")
        .slice(0, 1);
    }, []);

    const handleChange = useCallback(
      (index: number, inputValue: string) => {
        if (!onChange) return;
        const currentValue = value.split("");
        if (inputValue === "") {
          currentValue[index] = "";
        } else {
          const sanitized = sanitizeInput(inputValue);
          if (sanitized.length > 0) {
            currentValue[index] = sanitized;
          }
        }
        const newValue = currentValue.join("").padEnd(count, " ");
        onChange(newValue);
      },
      [onChange, sanitizeInput, value, count]
    );

    const handleKeyDown = useCallback(
      (index: number, e: React.KeyboardEvent) => {
        if (!onChange) return;
        if (e.key === "Backspace") {
          const newValue = value.split("");
          newValue[index] = "";
          onChange(newValue.join("").padEnd(count, " "));
        } else if (e.key === "ArrowLeft" && index > 0) {
          const nextInput = e.currentTarget.parentElement?.children[
            index - 1
          ] as HTMLInputElement;
          nextInput?.focus();
        } else if (e.key === "ArrowRight" && index < count - 1) {
          const nextInput = e.currentTarget.parentElement?.children[
            index + 1
          ] as HTMLInputElement;
          nextInput?.focus();
        }
      },
      [onChange, value, count]
    );

    const handleFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
      e.target.select();
    }, []);

    const isReadOnly = !onChange;

    if (printMode || isReadOnly) {
      return (
        <Box sx={{ display: "flex", gap: `${gapMm}mm` }}>
          {Array.from({ length: count }, (_, i) => (
            <Box
              key={i}
              sx={{
                width: `${boxSizeMm}mm`,
                height: `${boxSizeMm}mm`,
                border: "1px solid #000",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "14px",
                fontWeight: "bold",
                backgroundColor: "white",
              }}
            >
              {(value[i] ?? "").toString().toUpperCase()}
            </Box>
          ))}
        </Box>
      );
    }

    return (
      <Box sx={{ display: "flex", gap: `${gapMm}mm` }}>
        {Array.from({ length: count }, (_, i) => (
          <Input
            key={i}
            value={value[i] || ""}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onFocus={handleFocus}
            disabled={isReadOnly}
            sx={{
              width: `${boxSizeMm}mm`,
              height: `${boxSizeMm}mm`,
              border: "1px solid #000 !important",
              fontSize: "14px",
              lineHeight: 1,
              textAlign: "center",
              backgroundColor: isReadOnly ? "#f5f5f5" : "white",
              borderRadius: 0,
              boxSizing: "border-box",
              "& input": {
                textAlign: "center",
                padding: 0,
                backgroundColor: "transparent",
                color: isReadOnly ? "#666" : "inherit",
                cursor: isReadOnly ? "default" : "text",
                border: "none !important",
                outline: "none",
                width: "100%",
                height: "100%",
                boxSizing: "border-box",
                fontWeight: "bold",
              },
              "&:before": { display: "none !important" },
              "&:after": { display: "none !important" },
              "& fieldset": { display: "none !important" },
              "&:hover": {
                borderColor: isReadOnly ? "#000" : "#1976d2",
              },
              "&:focus-within": {
                borderColor: "#1976d2",
                borderWidth: "2px",
              },
            }}
            inputProps={{
              maxLength: 1,
              style: {
                textAlign: "center",
                fontSize: "14px",
                fontWeight: "bold",
              },
            }}
          />
        ))}
      </Box>
    );
  }
);

interface DateBoxesProps {
  value: { day: string; month: string; year: string };
  onChange?: (value: { day: string; month: string; year: string }) => void;
  printMode?: boolean;
}

const DateBoxes: React.FC<DateBoxesProps> = memo(
  ({ value, onChange, printMode = false }) => {
    const handleChange = useCallback(
      (field: "day" | "month" | "year", val: string) => {
        if (!onChange) return;
        const sanitized = val
          .replace(/[^0-9]/g, "")
          .slice(0, field === "year" ? 4 : 2);
        onChange({ ...value, [field]: sanitized });
      },
      [onChange, value]
    );

    if (printMode || !onChange) {
      return (
        <Box
          sx={{ display: "flex", alignItems: "center", gap: "1mm", ml: "10mm" }}
        >
          <Box
            sx={{
              width: "8mm",
              height: "6mm",
              border: "1px solid #000",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "12px",
              fontWeight: "bold",
              backgroundColor: "white",
            }}
          >
            {value.day}
          </Box>
          <Typography sx={{ fontSize: "10px", mx: 0.5 }}>/</Typography>
          <Box
            sx={{
              width: "8mm",
              height: "6mm",
              border: "1px solid #000",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "12px",
              fontWeight: "bold",
              backgroundColor: "white",
            }}
          >
            {value.month}
          </Box>
          <Typography sx={{ fontSize: "10px", mx: 0.5 }}>/</Typography>
          <Box
            sx={{
              width: "16mm",
              height: "6mm",
              border: "1px solid #000",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "12px",
              fontWeight: "bold",
              backgroundColor: "white",
            }}
          >
            {value.year}
          </Box>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "1mm",
          marginLeft: "10mm",
        }}
      >
        <Box
          sx={{
            width: "8mm",
            height: "6mm",
            border: "1px solid #000",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "white",
          }}
        >
          <Input
            value={value.day}
            onChange={(e) => handleChange("day", e.target.value)}
            sx={{
              width: "100%",
              height: "100%",
              fontSize: "12px",
              textAlign: "center",
              backgroundColor: "transparent",
              "& input": {
                textAlign: "center",
                padding: 0,
                backgroundColor: "transparent",
                border: "none",
                outline: "none",
                width: "100%",
                height: "100%",
                fontSize: "12px",
                fontWeight: "bold",
              },
              "&:before": { display: "none" },
              "&:after": { display: "none" },
              "& fieldset": { display: "none" },
            }}
            inputProps={{ maxLength: 2 }}
          />
        </Box>
        <Typography sx={{ fontSize: "10px", mx: 0.5 }}>/</Typography>
        <Box
          sx={{
            width: "8mm",
            height: "6mm",
            border: "1px solid #000",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "white",
          }}
        >
          <Input
            value={value.month}
            onChange={(e) => handleChange("month", e.target.value)}
            sx={{
              width: "100%",
              height: "100%",
              fontSize: "12px",
              textAlign: "center",
              backgroundColor: "transparent",
              "& input": {
                textAlign: "center",
                padding: 0,
                backgroundColor: "transparent",
                border: "none",
                outline: "none",
                width: "100%",
                height: "100%",
                fontSize: "12px",
                fontWeight: "bold",
              },
              "&:before": { display: "none" },
              "&:after": { display: "none" },
              "& fieldset": { display: "none" },
            }}
            inputProps={{ maxLength: 2 }}
          />
        </Box>
        <Typography sx={{ fontSize: "10px", mx: 0.5 }}>/</Typography>
        <Box
          sx={{
            width: "16mm",
            height: "6mm",
            border: "1px solid #000",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "white",
          }}
        >
          <Input
            value={value.year}
            onChange={(e) => handleChange("year", e.target.value)}
            sx={{
              width: "100%",
              height: "100%",
              fontSize: "12px",
              textAlign: "center",
              backgroundColor: "transparent",
              "& input": {
                textAlign: "center",
                padding: 0,
                backgroundColor: "transparent",
                border: "none",
                outline: "none",
                width: "100%",
                height: "100%",
                fontSize: "12px",
                fontWeight: "bold",
              },
              "&:before": { display: "none" },
              "&:after": { display: "none" },
              "& fieldset": { display: "none" },
            }}
            inputProps={{ maxLength: 4 }}
          />
        </Box>
      </Box>
    );
  }
);

interface TickProps {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

const Tick: React.FC<TickProps> = memo(({ label, checked, onChange }) => (
  <FormControlLabel
    control={
      <Checkbox
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        sx={{
          width: "6mm",
          height: "6mm",
          padding: 0,
          "& .MuiSvgIcon-root": { fontSize: "16px" },
        }}
      />
    }
    label={
      <Typography sx={{ fontSize: "12px", ml: 0.5, margin: "auto" }}>
        {label}
      </Typography>
    }
    sx={{ margin: 0, alignItems: "flex-start" }}
  />
));

interface MiniBoxesProps {
  count: number;
  value: string;
  onChange?: (value: string) => void;
  printMode?: boolean;
}

const MiniBoxes: React.FC<MiniBoxesProps> = ({
  count,
  value,
  onChange,
  printMode = false,
}) => {
  if (printMode || !onChange) {
    return (
      <Box sx={{ display: "flex", gap: "0.5mm" }}>
        {Array.from({ length: count }, (_, i) => (
          <Box
            key={i}
            sx={{
              width: "4mm",
              height: "4mm",
              border: "1px solid #000",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "8px",
              fontWeight: "bold",
              backgroundColor: "white",
            }}
          >
            {(value[i] ?? "").toString().toUpperCase()}
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", gap: "0.5mm" }}>
      {Array.from({ length: count }, (_, i) => (
        <Input
          key={i}
          value={value[i] || ""}
          onChange={(e) => {
            if (!onChange) return;
            const newValue = value.split("");
            newValue[i] = e.target.value.slice(0, 1).toUpperCase();
            onChange(newValue.join("").slice(0, count));
          }}
          sx={{
            width: "4mm",
            height: "4mm",
            border: "1px solid #000 !important",
            fontSize: "8px",
            textAlign: "center",
            borderRadius: 0,
            boxSizing: "border-box",
            "& input": {
              textAlign: "center",
              padding: 0,
              border: "none !important",
              outline: "none",
              width: "100%",
              height: "100%",
              boxSizing: "border-box",
            },
            "&:before": { display: "none !important" },
            "&:after": { display: "none !important" },
            "& fieldset": { display: "none !important" },
          }}
          inputProps={{ maxLength: 1 }}
        />
      ))}
    </Box>
  );
};

interface StudentData {
  id: number;
  applicationNo: string;
  rollNumber: string;
  enrolmentNo: string;
  studentName: string;
  fatherName: string;
  motherName: string;
  dateOfBirth: string | number[];
  gender: string;
  medium: string;
  caste: string;
  subCaste: string;
  casteCertificate: string;
  religion: string;
  sssmid: string;
  scholarNo: string;
  penNo: string;
  apparId: string;
  aadharNo: string;
  houseNo: string;
  street: string;
  landmark: string;
  area: string;
  city: string;
  postOffice: string;
  district: string;
  state: string;
  pinCode: string;
  mobileNo: string;
  bankName: string;
  ifcCode: string;
  accountNo: string;
  annualIncome: string;
  section: string;
  subjects: string | string[];
  gmail: string;
  admissionDate: string | number[];
  admissionClass: string;
  schoolId: number;
  schoolClassId: number;
  sessionName: string;
  sessionId: number;
  className: string;
  userId?: number;
  status: boolean;
  studentPhoto?: string | File;
  photoUrl?: string | null;
  signatureUrl?: string | null;
  skipStudentMediaFetch?: boolean;
  cwsnCode: string;
  sambalKramank: string;
}

interface Props {
  studentData?: StudentData;
  renderForPDF?: boolean;
}

const EleventhExamFormTemplate: React.FC<Props> = ({
  studentData,
  renderForPDF = false,
}) => {
  const templateRef = useRef<HTMLDivElement>(null);
  const [isDownloadingImage, setIsDownloadingImage] = useState(false);
  const [isDownloadingPdfFromImage, setIsDownloadingPdfFromImage] =
    useState(false);
  const [captureMode, setCaptureMode] = useState(false);
  const isPrint = captureMode || renderForPDF;

  // Debug logging for student data
  React.useEffect(() => {
  }, [studentData?.id, studentData?.studentName]);

  const shouldFetchPhoto =
    !!studentData?.id && !studentData?.photoUrl && !studentData?.skipStudentMediaFetch;
  const studentIdForPhotoFetch = shouldFetchPhoto ? studentData?.id : undefined;
  const {
    imageUrl: authenticatedImageUrl,
    isLoading: photoLoading,
    error: imageError,
  } = useStudentPhoto(studentIdForPhotoFetch);

  const shouldFetchSignature =
    !!studentData?.id && !studentData?.signatureUrl && !studentData?.skipStudentMediaFetch;
  const studentIdForSignatureFetch = shouldFetchSignature ? studentData?.id : undefined;
  const {
    signatureUrl: authenticatedSignatureUrl,
    isLoading: signatureLoading,
    error: signatureError,
    hasSignature,
  } = useStudentSignature(studentIdForSignatureFetch);

  const { data: subjects = [] } = useSubjectsByClassId(
    studentData?.schoolClassId || 0
  );

  const subjectCodeToName = React.useMemo(() => {
    const mapping: { [key: string]: string } = {};
    subjects.forEach((subject: any) => {
      mapping[subject.subjectCode] = subject.name;
    });
    return mapping;
  }, [subjects]);

  const getSubjectNames = React.useCallback(
    (subjectCodes: string | string[]) => {
      if (!subjectCodes) return [];
      const codes = Array.isArray(subjectCodes) ? subjectCodes : [subjectCodes];
      return codes
        .map((code) => subjectCodeToName[code] || code)
        .filter(Boolean);
    },
    [subjectCodeToName]
  );

  const languageSubjects = [
    "Hindi",
    "English",
    "Marathi",
    "Gujrati",
    "Punjabi",
    "Urdu",
    "Sindhi",
    "Sanskrit",
  ];

  const { user, isSchoolAdmin } = useAuth();

  const shouldFetchAdminSchoolId = !!user && isSchoolAdmin() && !user.schoolId;
  const adminSchoolIdQuery = useSchoolIdForAdmin(
    shouldFetchAdminSchoolId ? user?.id : undefined
  );
  const effectiveSchoolId =
    user?.schoolId ?? adminSchoolIdQuery.data ?? studentData?.schoolId;
  const { useSchoolById } = useSchools();
  const { data: schoolData } = useSchoolById(effectiveSchoolId);

  const [form, setForm] = useState({
    applicantTypeCode: "",
    isMPResident: false,
    cwsnCode: "",
    section: "",
    previousYear: "",
    previousRollNo: "",
    previousBoard: "",
    previousResult: "",
    previousGrade: "",
    candidateName: "",
    fatherName: "",
    motherName: "",
    dateOfBirth: { day: "", month: "", year: "" },
    dateOfBirthInWords: "",
    gender: "",
    mediumCode: "",
    blockName: "",
    districtName: "",
    classSubjectCode: "",
    examCenterCode: "",
    districtCode: "",
    vikasKhandCode: "",
    shalaAgrasenCode: "",
    namankanCode: "",
    currentDate: "",
    institutionCode: "",
    institutionName: "",
    firstLanguage: { first: "", second: "" },
    secondLanguage: { first: "", second: "" },
    thirdLanguage: { first: "", second: "" },
    ssmid: "",
    sambalNo: "",
    organizationNo: "",
    apparId: "",
    houseNo: "",
    street: "",
    area: "",
    city: "",
    district: "",
    mobileNo: "",
    pinCode: "",
    caste: "",
    q11: "",
    q12: "",
    q13: "",
    q14: "",
    q15: "",
    q16: "",
    q17: "",
    declarationSignature: "",
    principalSeal: "",
    principalDate: "",
    principalSignature: "",
    deaf: "",
    dumb: "",
    subjects: [] as string[],
  });

  useEffect(() => {
    if (!schoolData) return;
    const schoolCode = schoolData.schoolCode || "";
    const schoolName = schoolData.nameofSchool || "";
    const today = new Date();
    const day = today.getDate().toString().padStart(2, "0");
    const month = (today.getMonth() + 1).toString().padStart(2, "0");
    const year = today.getFullYear();
    const currentDate = `${day}/${month}/${year}`;

    const districtName =
      (schoolData as any)?.districtName ?? (schoolData as any)?.district ?? "";
    const blockName =
      (schoolData as any)?.blockName ?? (schoolData as any)?.block ?? "";

    setForm((prev) => ({
      ...prev,
      currentDate,
      institutionCode: schoolCode.slice(0, 6),
      institutionName: schoolName,
      districtCode:
        schoolData.districtCode || schoolCode.slice(0, 2).padEnd(2, "0"),
      vikasKhandCode:
        schoolData.blockCode || schoolCode.slice(0, 4).padEnd(4, "0"),
      shalaAgrasenCode: schoolCode.slice(0, 6).padEnd(6, "0"),
      districtName,
      blockName,
    }));
  }, [schoolData]);

  useEffect(() => {
    if (!studentData) return;
    const enrollmentNo = studentData.enrolmentNo || "";
    setForm((prev) => ({
      ...prev,
      namankanCode: enrollmentNo,
    }));
  }, [studentData]);

  const categorizeSubjects = React.useCallback(() => {
    const subjectNames = getSubjectNames(form.subjects);
    const languageSubjectsFound: Array<{
      name: string;
      code: string;
      index: number;
    }> = [];
    const nonLanguageSubjects: Array<{
      name: string;
      code: string;
      index: number;
    }> = [];

    subjectNames.forEach((subjectName, index) => {
      const isLanguage = languageSubjects.some((lang) =>
        subjectName.toLowerCase().includes(lang.toLowerCase())
      );
      if (isLanguage) {
        languageSubjectsFound.push({
          name: subjectName,
          code: form.subjects[index] || "",
          index,
        });
      } else {
        nonLanguageSubjects.push({
          name: subjectName,
          code: form.subjects[index] || "",
          index,
        });
      }
    });

    return { languageSubjectsFound, nonLanguageSubjects };
  }, [form.subjects, getSubjectNames]);

  const { languageSubjectsFound, nonLanguageSubjects } = categorizeSubjects();

  const firstLanguage = languageSubjectsFound[0];
  const secondLanguage = languageSubjectsFound[1];

  const subjectRowsForUi = nonLanguageSubjects.slice(0, 4);
  const firstSubject = subjectRowsForUi[0];
  const secondSubject = subjectRowsForUi[1];
  const thirdSubject = subjectRowsForUi[2];
  const additionalSubject = subjectRowsForUi[3];

  useEffect(() => {
    if (!studentData) return;

    const fullName = studentData.studentName || "";
    const fatherName = studentData.fatherName || "";
    const motherName = studentData.motherName || "";

    let dob = { day: "", month: "", year: "" };
    if (studentData.dateOfBirth) {
      if (Array.isArray(studentData.dateOfBirth)) {
        const [y, m, d] = studentData.dateOfBirth;
        dob = {
          day: String(d).padStart(2, "0"),
          month: String(m).padStart(2, "0"),
          year: String(y),
        };
      } else {
        const date = new Date(studentData.dateOfBirth);
        dob = {
          day: String(date.getDate()).padStart(2, "0"),
          month: String(date.getMonth() + 1).padStart(2, "0"),
          year: String(date.getFullYear()),
        };
      }
    }

    let genderCode = "";
    const g = (studentData.gender || "").toLowerCase();
    if (g === "m" || g === "male") genderCode = "1";
    else if (g === "f" || g === "female") genderCode = "2";

    let casteCode = "";
    const c = (studentData.caste || "").toLowerCase();
    if (c === "sc" || c.includes("scheduled caste")) casteCode = "1";
    else if (c === "st" || c.includes("scheduled tribe")) casteCode = "2";
    else if (c === "obc" || c.includes("backward")) casteCode = "3";
    else casteCode = "4";

    let mediumCode = "";
    const m = (studentData.medium || "").toLowerCase();
    if (m.includes("hindi")) mediumCode = "1";
    else if (m.includes("english")) mediumCode = "2";
    else if (m.includes("urdu")) mediumCode = "3";

    const dateInWords = convertDateToHindiWords(formatDateFromForm(dob));

    setForm((prev) => ({
      ...prev,
      candidateName: fullName,
      fatherName,
      motherName,
      dateOfBirth: dob,
      dateOfBirthInWords: dateInWords,
      gender: genderCode,
      caste: casteCode,
      mediumCode,
      ssmid: studentData.sssmid || "",
      mobileNo: studentData.mobileNo || "",
      houseNo: studentData.houseNo || "",
      street: studentData.street || "",
      area: studentData.area || "",
      city: studentData.city || "",
      district: studentData.district || "",
      pinCode: studentData.pinCode || "",
      apparId: studentData.apparId || "",
      section: studentData.section || "",
      subjects: Array.isArray(studentData.subjects)
        ? studentData.subjects
        : studentData.subjects
        ? [studentData.subjects]
        : [],
      cwsnCode: studentData.cwsnCode || "",
      sambalNo: studentData.sambalKramank || "",
    }));
  }, [studentData]);

  useEffect(() => {
    const { day, month, year } = form.dateOfBirth;
    if (day && month && year) {
      const dateString = formatDateFromForm(form.dateOfBirth);
      const inWords = convertDateToHindiWords(dateString);
      if (inWords !== form.dateOfBirthInWords) {
        setForm((p) => ({ ...p, dateOfBirthInWords: inWords }));
      }
    }
  }, [form.dateOfBirth, form.dateOfBirthInWords]);

  const updateForm = useCallback((field: string, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const downloadAsImage = async () => {
    if (!templateRef.current) return;
    setIsDownloadingImage(true);
    setCaptureMode(true);
    try {
      await (document as any).fonts?.ready?.catch(() => {});
      await new Promise((r) => setTimeout(r, 80));
      const canvas = await html2canvas(templateRef.current, {
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        background: "#ffffff",
        logging: false,
        scale: 2,
        width: templateRef.current.scrollWidth,
        height: templateRef.current.scrollHeight,
        scrollX: 0,
        scrollY: 0,
        windowWidth: templateRef.current.scrollWidth,
        windowHeight: templateRef.current.scrollHeight,
      } as any);
      const link = document.createElement("a");
      link.download = `eleventh-exam-form-${
        studentData?.studentName || "student"
      }-${new Date().toISOString().split("T")[0]}.png`;
      link.href = canvas.toDataURL("image/png", 1.0);
      link.click();
    } catch (e) {
      alert("Failed to download image. Please try again.");
    } finally {
      setCaptureMode(false);
      setIsDownloadingImage(false);
    }
  };

  const downloadAsPdfFromImage = async () => {
    if (!templateRef.current) return;
    setIsDownloadingPdfFromImage(true);
    setCaptureMode(true);
    try {
      await (document as any).fonts?.ready?.catch(() => {});
      await new Promise((r) => setTimeout(r, 80));
      const element = templateRef.current;
      const opt = {
        margin: [0, 0, 0, 0],
        filename: `eleventh-exam-form-${studentData?.studentName || "student"}-${
          new Date().toISOString().split("T")[0]
        }.pdf`,
        image: { type: "jpeg", quality: 1.0 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: "#ffffff",
          width: element.scrollWidth,
          height: element.scrollHeight,
          scrollX: 0,
          scrollY: 0,
          windowWidth: element.scrollWidth,
          windowHeight: element.scrollHeight,
          logging: false,
        },
        jsPDF: {
          unit: "mm",
          format: "a4",
          orientation: "portrait",
          compress: false,
          putOnlyUsedFonts: true,
        },
        pagebreak: { mode: ["css", "legacy"], before: ".page-break" },
      };
      await html2pdf().set(opt).from(element).save();
    } catch (e) {
      alert("Failed to download PDF. Please try again.");
    } finally {
      setCaptureMode(false);
      setIsDownloadingPdfFromImage(false);
    }
  };

  if (!studentData) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary">
          No student data provided
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Please select a student to view the exam form.
        </Typography>
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Paper
        ref={templateRef}
        data-pdf-root
        sx={{
          width: "210mm",
          minHeight: "297mm",
          padding: "0mm",
          margin: "0 auto",
          position: "relative",
          backgroundColor: "white",
          "*": { boxSizing: "border-box" },
          fontFeatureSettings: "'liga' 1, 'clig' 1, 'kern' 1",
          WebkitFontSmoothing: "antialiased",
          textRendering: "optimizeLegibility",
          "& .avoid-break": { breakInside: "avoid", pageBreakInside: "avoid" },
          "& .allow-break": { breakInside: "auto", pageBreakInside: "auto" },
          "@media print": {
            boxShadow: "none",
            backgroundColor: "white",
            margin: 0,
            padding: "3mm",
          },
        }}
      >
        <Box sx={{ textAlign: "center", mb: 1, backgroundColor: "white" }}>
          <Box
            sx={{
              width: "20mm",
              height: "20mm",
              margin: "0 auto 0mm",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img
              src={process.env.PUBLIC_URL + "/tenthExamFormLogo.png"}
              alt="MP Board Logo"
              crossOrigin="anonymous"
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
            />
          </Box>
          <Typography sx={{ fontSize: "16px", fontWeight: "bold" }}>
            माध्यमिक शिक्षा मण्डल, मध्यप्रदेश, भोपाल
          </Typography>
          <Typography sx={{ fontSize: "14px", fontWeight: "bold" }}>
            नामांकन आवेदन पत्र कक्षा 11वीं
          </Typography>
        </Box>

        <Grid container spacing={0.2} sx={{ backgroundColor: "white", mx: 0 }}>
          <Grid item xs={12}>
            <Box
              sx={{ px: 0.3, position: "relative", backgroundColor: "white" }}
            >
              <Box sx={{ display: "flex", alignItems: "flex-start", mb: 1 }}>
                <Box sx={{ width: "36mm", flexShrink: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 0.6,
                      mt: 0.5,
                    }}
                  >
                    <Label sx={{ fontSize: "13px" }}>1. आवेदक का प्रकार</Label>
                    <Cell width="16mm" height="10mm">
                      <input
                        type="text"
                        value={form.applicantTypeCode}
                        onChange={(e) =>
                          updateForm("applicantTypeCode", e.target.value)
                        }
                        style={{
                          width: "100%",
                          height: "100%",
                          border: "none",
                          outline: "none",
                          textAlign: "center",
                          fontSize: "10px",
                          fontWeight: "bold",
                          background: "transparent",
                        }}
                        maxLength={1}
                      />
                    </Cell>
                  </Box>
                </Box>

                <Box sx={{ flex: 1, ml: 1 }}>
                  <Box
                    sx={{
                      border: "1px solid #000",
                      p: 0.5,
                      mb: 0.8,
                      backgroundColor: "white",
                      width: "40%",
                    }}
                  >
                    <Typography
                      sx={{ fontSize: "14px", fontWeight: "bolder", mb: 0.5 }}
                    >
                      संबंधित कोड नम्बर ही खाने में भरिये
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-around",
                        flexWrap: "wrap",
                        gap: 1,
                      }}
                    >
                      <Typography sx={{ fontSize: "12px", fontWeight: "bold" }}>
                        नियमित - 1
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>

              <Box
                className="avoid-break"
                sx={{
                  display: "flex",
                  justifyContent: "space-around",
                  alignItems: "center",
                  mb: 1,
                  gap: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Label sx={{ fontSize: "14px", fontWeight: "bolder" }}>
                    मध्यप्रदेश के मूलनिवासी
                  </Label>
                  <Typography
                    sx={{ fontSize: "12px", ml: 1, fontWeight: "bold" }}
                  >
                    हाँ/नहीं
                  </Typography>
                  <Tick
                    label=""
                    checked={form.isMPResident}
                    onChange={(checked) => updateForm("isMPResident", checked)}
                  />
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Label sx={{ fontSize: "14px", fontWeight: "bolder" }}>
                    CWSN Code
                  </Label>
                  <Box
                    sx={{
                      width: "20mm",
                      height: "7mm",
                      border: "1px solid #000",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <input
                      type="text"
                      value={studentData?.cwsnCode || ""}
                      readOnly
                      style={{
                        width: "100%",
                        height: "100%",
                        border: "none",
                        outline: "none",
                        textAlign: "center",
                        fontSize: "13px",
                        fontWeight: "bold",
                        background: "transparent",
                      }}
                    />
                  </Box>
                  <Typography sx={{ fontSize: "10px" }}>
                    (Ref. Point 11)
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ mb: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "start",
                    mb: 0.5,
                  }}
                >
                  <Typography
                    sx={{ fontSize: "13px", mb: 1, ml: 3, fontWeight: "bold" }}
                  >
                    क्या अर्हकारी परीक्षा अन्य बोर्ड/अन्य राज्य से उत्तीर्ण की
                    है, यदि हाँ तो बोर्ड एवं वर्ष की जानकारी अंकित करें।
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-around",
                    gap: 2,
                    mb: 1,
                    alignItems: "center",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "start",
                      gap: 1,
                      alignItems: "center",
                      mb: 0.5,
                    }}
                  >
                    <Label sx={{ fontSize: "14px", fontWeight: "bold" }}>
                      वर्ष
                    </Label>
                    <Box
                      sx={{
                        width: "35mm",
                        height: "6mm",
                        border: "1px solid #000",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <input
                        type="text"
                        value={form.previousRollNo}
                        onChange={(e) =>
                          updateForm("previousRollNo", e.target.value)
                        }
                        style={{
                          width: "100%",
                          height: "100%",
                          border: "none",
                          outline: "none",
                          textAlign: "center",
                          fontSize: "13px",
                          fontWeight: "bold",
                          background: "transparent",
                        }}
                      />
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      // flexDirection: "column",
                      justifyContent: "start",
                      gap: 1,
                      alignItems: "center",
                      mb: 0.5,
                    }}
                  >
                    <Label sx={{ fontSize: "14px", fontWeight: "bold" }}>
                      परिणाम
                    </Label>
                    <Box
                      sx={{
                        width: "40mm",
                        height: "6mm",
                        border: "1px solid #000",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <input
                        type="text"
                        value={form.previousBoard}
                        onChange={(e) =>
                          updateForm("previousBoard", e.target.value)
                        }
                        style={{
                          width: "100%",
                          height: "100%",
                          border: "none",
                          outline: "none",
                          textAlign: "center",
                          fontSize: "13px",
                          fontWeight: "bold",
                          background: "transparent",
                        }}
                      />
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      // flexDirection: "column",
                      justifyContent: "start",
                      gap: 1,
                      alignItems: "center",
                      mb: 0.5,
                    }}
                  >
                    <Label sx={{ fontSize: "14px", fontWeight: "bold" }}>
                      श्रेणी
                    </Label>
                    <Box
                      sx={{
                        width: "40mm",
                        height: "6mm",
                        border: "1px solid #000",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <input
                        type="text"
                        value={form.previousBoard}
                        onChange={(e) =>
                          updateForm("previousBoard", e.target.value)
                        }
                        style={{
                          width: "100%",
                          height: "100%",
                          border: "none",
                          outline: "none",
                          textAlign: "center",
                          fontSize: "13px",
                          fontWeight: "bold",
                          background: "transparent",
                        }}
                      />
                    </Box>
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-around",
                    gap: 2,
                    alignItems: "center",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "start",
                      gap: 1,
                      alignItems: "center",
                      mb: 0.5,
                    }}
                  >
                    <Label sx={{ fontSize: "14px", fontWeight: "bold" }}>
                      रोल नम्बर
                    </Label>
                    <Box
                      sx={{
                        width: "35mm",
                        height: "6mm",
                        border: "1px solid #000",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <input
                        type="text"
                        value={form.previousRollNo}
                        onChange={(e) =>
                          updateForm("previousRollNo", e.target.value)
                        }
                        style={{
                          width: "100%",
                          height: "100%",
                          border: "none",
                          outline: "none",
                          textAlign: "center",
                          fontSize: "13px",
                          fontWeight: "bold",
                          background: "transparent",
                        }}
                      />
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "start",
                      gap: 1,
                      alignItems: "center",
                      mb: 0.5,
                    }}
                  >
                    <Label sx={{ fontSize: "14px", fontWeight: "bold" }}>
                      Board Name
                    </Label>
                    <Box
                      sx={{
                        width: "70mm",
                        height: "6mm",
                        border: "1px solid #000",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <input
                        type="text"
                        value={form.previousBoard}
                        onChange={(e) =>
                          updateForm("previousBoard", e.target.value)
                        }
                        style={{
                          width: "100%",
                          height: "100%",
                          border: "none",
                          outline: "none",
                          textAlign: "center",
                          fontSize: "13px",
                          fontWeight: "bold",
                          background: "transparent",
                        }}
                      />
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "start",
                      gap: 1,
                      alignItems: "center",
                      mb: 0.5,
                    }}
                  >
                    <Label sx={{ fontSize: "14px", fontWeight: "bold" }}>
                      Samagra
                    </Label>
                    <Box
                      sx={{
                        width: "70mm",
                        height: "6mm",
                        border: "1px solid #000",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <input
                        type="text"
                        value={form.ssmid || studentData?.sssmid || ""}
                        readOnly
                        style={{
                          width: "100%",
                          height: "100%",
                          border: "none",
                          outline: "none",
                          textAlign: "center",
                          fontSize: "13px",
                          fontWeight: "bold",
                          background: "transparent",
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* Section 2 – Candidate name */}
          <Grid item xs={12}>
            <Box
              sx={{ p: 0.7, position: "relative", backgroundColor: "white" }}
            >
              <Box sx={{ display: "flex", gap: 0.4, flexDirection: "column" }}>
                <Row label="2. आवेदक का पूर्ण नाम / FULL NAME OF CANDIDATE" />
                <CharBoxes
                  count={37}
                  value={form.candidateName.trim()}
                  onChange={(value) => updateForm("candidateName", value)}
                  printMode={isPrint}
                />
              </Box>
            </Box>
          </Grid>

          {/* Section 3 – Father */}
          <Grid item xs={12}>
            <Box
              sx={{ p: 0.7, position: "relative", backgroundColor: "white" }}
            >
              <Box sx={{ display: "flex", gap: 0.4, flexDirection: "column" }}>
                <Row label="3. पिता का पूर्ण नाम / FULL NAME OF FATHER" />
                <CharBoxes
                  count={37}
                  value={form.fatherName.trim()}
                  onChange={(value) => updateForm("fatherName", value)}
                  printMode={isPrint}
                />
              </Box>
            </Box>
          </Grid>

          {/* Section 4 – Mother */}
          <Grid item xs={12}>
            <Box
              sx={{ p: 0.7, position: "relative", backgroundColor: "white" }}
            >
              <Row label="4. माता का पूर्ण नाम / FULL NAME OF MOTHER" />
              <CharBoxes
                count={37}
                value={form.motherName.trim()}
                onChange={(value) => updateForm("motherName", value)}
                printMode={isPrint}
              />
            </Box>
          </Grid>

          {/* Section 5 – DOB + in words */}
          <Grid item xs={12}>
            <Box sx={{ p: 1, position: "relative", backgroundColor: "white" }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box sx={{ display: "flex" }}>
                  <Box>
                    <Label>5. जन्म तिथि</Label>
                    <Label> (अंको में)</Label>
                  </Box>
                  <Box>
                    <DateBoxes
                      value={form.dateOfBirth}
                      onChange={(value) => updateForm("dateOfBirth", value)}
                      printMode={isPrint}
                    />
                  </Box>
                </Box>

                <Box sx={{ display: "flex" }}>
                  <Box sx={{ ml: 8 }}>
                    <Label>जन्म तिथि</Label>
                    <Label>(शब्दों में)</Label>
                  </Box>
                  <Box>
                    {isPrint ? (
                      <Cell
                        width="300px"
                        height="30px"
                        sx={{ justifyContent: "flex-start", px: 1, ml: "10mm" }}
                      >
                        <PrintText sx={{ fontSize: "14px", fontWeight: 700 }}>
                          {form.dateOfBirthInWords}
                        </PrintText>
                      </Cell>
                    ) : (
                      <Input
                        value={form.dateOfBirthInWords}
                        readOnly
                        sx={{
                          width: "300px",
                          height: "30px",
                          fontSize: "14px",
                          border: "1px solid #000",
                          borderRadius: "2px",
                          marginLeft: "10mm",
                          px: 1,
                          "&:before": { borderBottom: "none" },
                          "&:after": { borderBottom: "none" },
                          "&:hover:not(.Mui-disabled):before": {
                            borderBottom: "none",
                          },
                        }}
                      />
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* Section 6 – Codes + brief note area (trimmed) */}
          <Grid item xs={12}>
            <Box sx={{ p: 1, position: "relative", backgroundColor: "white" }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box
                  className="avoid-break"
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    alignItems: "center",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      gap: 4,
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.7,
                        alignItems: "center",
                      }}
                    >
                      <Label>6. जिला कोड</Label>
                      <CharBoxes
                        count={2}
                        value={form.districtCode}
                        onChange={(value) => updateForm("districtCode", value)}
                        printMode={isPrint}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.7,
                        alignItems: "center",
                      }}
                    >
                      <Label>विकास खण्ड कोड</Label>
                      <CharBoxes
                        count={4}
                        value={form.vikasKhandCode}
                        onChange={(value) =>
                          updateForm("vikasKhandCode", value)
                        }
                        printMode={isPrint}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.7,
                        alignItems: "center",
                      }}
                    >
                      <Label>शाला अग्रसेन संस्था कोड</Label>
                      <CharBoxes
                        count={6}
                        value={form.shalaAgrasenCode}
                        onChange={(value) =>
                          updateForm("shalaAgrasenCode", value)
                        }
                        printMode={isPrint}
                      />
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 4,
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "start",
                        gap: 1,
                        alignItems: "center",
                        mb: 0.5,
                      }}
                    >
                      <Label sx={{ fontSize: "14px", fontWeight: "bold" }}>
                        जिला का नाम
                      </Label>
                      <Box
                        sx={{
                          width: "40mm",
                          height: "7mm",
                          border: "1px solid #000",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <input
                          type="text"
                          value={form.districtName || studentData?.district || ""}
                          readOnly
                          style={{
                            width: "100%",
                            height: "100%",
                            border: "none",
                            outline: "none",
                            textAlign: "center",
                            fontSize: "13px",
                            fontWeight: "bold",
                            background: "transparent",
                          }}
                        />
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "start",
                        gap: 1,
                        alignItems: "center",
                        mb: 0.5,
                      }}
                    >
                      <Label sx={{ fontSize: "14px", fontWeight: "bold" }}>
                        विकास खण्ड का नाम
                      </Label>
                      <Box
                        sx={{
                          width: "40mm",
                          height: "7mm",
                          border: "1px solid #000",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <input
                          type="text"
                          value={form.previousBoard}
                          onChange={(e) =>
                            updateForm("previousBoard", e.target.value)
                          }
                          style={{
                            width: "100%",
                            height: "100%",
                            border: "none",
                            outline: "none",
                            textAlign: "center",
                            fontSize: "13px",
                            fontWeight: "bold",
                            background: "transparent",
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Box>
                <Grid item xs={5} sm={3}>
                  <Box border={1} px={2} py={1} height="100%">
                    <Label
                      sx={{
                        fontSize: "14px",
                        fontWeight: "bold",
                        display: "flex",
                        justifyContent: "center",
                        mt: 1,
                      }}
                    >
                      माध्यम
                    </Label>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "start",
                      }}
                    >
                      <Tick
                        label={`Hindi - 1`}
                        checked={form.mediumCode === "1"}
                        onChange={(c) => updateForm("mediumCode", c ? "1" : "")}
                      />
                      <Tick
                        label={`English - 2`}
                        checked={form.mediumCode === "2"}
                        onChange={(c) => updateForm("mediumCode", c ? "2" : "")}
                      />
                      <Tick
                        label={`Urdu - 3`}
                        checked={form.mediumCode === "3"}
                        onChange={(c) => updateForm("mediumCode", c ? "3" : "")}
                      />
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={5} sm={2}>
                  <Box border={1} px={2} py={1} height="100%">
                    <Label
                      sx={{
                        fontSize: "14px",
                        fontWeight: "bold",
                        display: "flex",
                        justifyContent: "center",
                        mt: 1,
                      }}
                    >
                      Section
                    </Label>
                    <Box sx={{ display: "flex", flexDirection: "column",mt: 1 ,justifyContent: "center" ,alignItems: "center"}}>
                      <Box
                        sx={{
                          width: "15mm",
                          height: "7mm",
                          border: "1px solid #000",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <input
                          type="text"
                          value={studentData?.section || ""}
                          readOnly
                          style={{
                            width: "100%",
                            height: "100%",
                            border: "none",
                            outline: "none",
                            textAlign: "center",
                            fontSize: "13px",
                            fontWeight: "bold",
                            background: "transparent",
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              </Box>
            </Box>
          </Grid>

          {/* Section 7 – Language & Subjects layout (11th class style) */}
          <Grid item xs={12}>
            <Box sx={{ px: 1, position: "relative", backgroundColor: "white" }}>
              <Typography
                sx={{ fontSize: "9", fontWeight: "bold", mb: 0.7 }}
              >
                7  भाषा  विषय  :-
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                {/* Left side – प्रथम / द्वितीय भाषा */}
                <Box sx={{ flex: 1, pr: 2 }}>
                  {/* प्रथम भाषा */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 0.5,
                      gap: 1,
                    }}
                  >
                    <Label sx={{ minWidth: "32mm" }}>प्रथम भाषा</Label>
                    <Box
                      sx={{
                        flex: 1,
                        borderBottom: "1px solid #000",
                        mr: 2,
                      }}
                    >
                      <input
                        type="text"
                        value={firstLanguage?.name || ""}
                        readOnly
                        style={{
                          border: "none",
                          outline: "none",
                          fontSize: "14px",
                          padding: "0px 4px",
                          background: "transparent",
                          width: "100%",
                        }}
                      />
                    </Box>
                    <MiniBoxes
                      count={3}
                      value={firstLanguage?.code || ""}
                      onChange={
                        firstLanguage
                          ? (value) => {
                              const newSubjects = [...form.subjects];
                              newSubjects[firstLanguage.index] = value;
                              updateForm("subjects", newSubjects);
                            }
                          : undefined
                      }
                      printMode={isPrint || !firstLanguage}
                    />
                  </Box>

                  {/* द्वितीय भाषा */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 1,
                      gap: 1,
                    }}
                  >
                    <Label sx={{ minWidth: "32mm" }}>द्वितीय भाषा</Label>
                    <Box
                      sx={{
                        flex: 1,
                        borderBottom: "1px solid #000",
                        mr: 2,
                      }}
                    >
                      <input
                        type="text"
                        value={secondLanguage?.name || ""}
                        readOnly
                        style={{
                          border: "none",
                          outline: "none",
                          fontSize: "14px",
                          padding: "0px 4px",
                          background: "transparent",
                          width: "100%",
                        }}
                      />
                    </Box>
                    <MiniBoxes
                      count={3}
                      value={secondLanguage?.code || ""}
                      onChange={
                        secondLanguage
                          ? (value) => {
                              const newSubjects = [...form.subjects];
                              newSubjects[secondLanguage.index] = value;
                              updateForm("subjects", newSubjects);
                            }
                          : undefined
                      }
                      printMode={isPrint || !secondLanguage}
                    />
                  </Box>
                </Box>

                {/* Right side – मुख्य / अतिरिक्त विषय */}
                <Box sx={{ flex: 1, pl: 2, borderLeft: "1px solid #000" }}>
                  {/* प्रथम विषय */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 0.5,
                      gap: 1,
                    }}
                  >
                    <Label sx={{ minWidth: "32mm" }}>प्रथम विषय</Label>
                    <Box
                      sx={{
                        flex: 1,
                        borderBottom: "1px solid #000",
                        mr: 2,
                      }}
                    >
                      <input
                        type="text"
                        value={firstSubject?.name || ""}
                        readOnly
                        style={{
                          border: "none",
                          outline: "none",
                          fontSize: "14px",
                          padding: "0px 4px",
                          background: "transparent",
                          width: "100%",
                        }}
                      />
                    </Box>
                    <MiniBoxes
                      count={3}
                      value={firstSubject?.code || ""}
                      onChange={
                        firstSubject
                          ? (value) => {
                              const newSubjects = [...form.subjects];
                              newSubjects[firstSubject.index] = value;
                              updateForm("subjects", newSubjects);
                            }
                          : undefined
                      }
                      printMode={isPrint || !firstSubject}
                    />
                  </Box>

                  {/* द्वितीय विषय */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 0.5,
                      gap: 1,
                    }}
                  >
                    <Label sx={{ minWidth: "32mm" }}>द्वितीय विषय</Label>
                    <Box
                      sx={{
                        flex: 1,
                        borderBottom: "1px solid #000",
                        mr: 2,
                      }}
                    >
                      <input
                        type="text"
                        value={secondSubject?.name || ""}
                        readOnly
                        style={{
                          border: "none",
                          outline: "none",
                          fontSize: "14px",
                          padding: "0px 4px",
                          background: "transparent",
                          width: "100%",
                        }}
                      />
                    </Box>
                    <MiniBoxes
                      count={3}
                      value={secondSubject?.code || ""}
                      onChange={
                        secondSubject
                          ? (value) => {
                              const newSubjects = [...form.subjects];
                              newSubjects[secondSubject.index] = value;
                              updateForm("subjects", newSubjects);
                            }
                          : undefined
                      }
                      printMode={isPrint || !secondSubject}
                    />
                  </Box>

                  {/* तृतीय विषय */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 0.5,
                      gap: 1,
                    }}
                  >
                    <Label sx={{ minWidth: "32mm" }}>तृतीय विषय</Label>
                    <Box
                      sx={{
                        flex: 1,
                        borderBottom: "1px solid #000",
                        mr: 2,
                      }}
                    >
                      <input
                        type="text"
                        value={thirdSubject?.name || ""}
                        readOnly
                        style={{
                          border: "none",
                          outline: "none",
                          fontSize: "14px",
                          padding: "0px 4px",
                          background: "transparent",
                          width: "100%",
                        }}
                      />
                    </Box>
                    <MiniBoxes
                      count={3}
                      value={thirdSubject?.code || ""}
                      onChange={
                        thirdSubject
                          ? (value) => {
                              const newSubjects = [...form.subjects];
                              newSubjects[thirdSubject.index] = value;
                              updateForm("subjects", newSubjects);
                            }
                          : undefined
                      }
                      printMode={isPrint || !thirdSubject}
                    />
                  </Box>

                  {/* अतिरिक्त विषय */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Label sx={{ minWidth: "32mm" }}>अतिरिक्त विषय</Label>
                    <Box
                      sx={{
                        flex: 1,
                        borderBottom: "1px solid #000",
                        mr: 2,
                      }}
                    >
                      <input
                        type="text"
                        value={additionalSubject?.name || ""}
                        readOnly
                        style={{
                          border: "none",
                          outline: "none",
                          fontSize: "14px",
                          padding: "0px 4px",
                          background: "transparent",
                          width: "100%",
                        }}
                      />
                    </Box>
                    <MiniBoxes
                      count={3}
                      value={additionalSubject?.code || ""}
                      onChange={
                        additionalSubject
                          ? (value) => {
                              const newSubjects = [...form.subjects];
                              newSubjects[additionalSubject.index] = value;
                              updateForm("subjects", newSubjects);
                            }
                          : undefined
                      }
                      printMode={isPrint || !additionalSubject}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* Section 9 – Address + Photo/Signature boxes */}
          <Grid item xs={12}>
            <Box sx={{ p: 1, position: "relative", backgroundColor: "white" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: "9", fontWeight: "bold" }}>
                    9. पता / Address
                  </Typography>

                  <Box sx={{ mb: 0.7 }}>
                    <Label>House No.</Label>
                    <CharBoxes
                      count={27}
                      value={form.houseNo}
                      onChange={(v) => updateForm("houseNo", v)}
                      printMode={isPrint}
                    />
                  </Box>

                  <Box sx={{ mb: 0.7 }}>
                    <Label>Colony</Label>
                    <CharBoxes
                      count={27}
                      value={form.street}
                      onChange={(v) => updateForm("street", v)}
                      printMode={isPrint}
                    />
                  </Box>

                  <Box sx={{ mb: 0.7 }}>
                    <Label>Town</Label>
                    <CharBoxes
                      count={27}
                      value={form.city}
                      onChange={(v) => updateForm("city", v)}
                      printMode={isPrint}
                    />
                  </Box>

                  <Box sx={{ mb: 0.7 }}>
                    <Label>Block</Label>
                    <CharBoxes
                      count={27}
                      value={form.district}
                      onChange={(v) => updateForm("district", v)}
                      printMode={isPrint}
                    />
                  </Box>

                  <Box sx={{ mb: 0.7 }}>
                    <Label>District</Label>
                    <CharBoxes
                      count={27}
                      value={form.district}
                      onChange={(v) => updateForm("district", v)}
                      printMode={isPrint}
                    />
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      mb: 0.7,
                      justifyContent: "space-between",
                    }}
                  >
                    <Box>
                      <Label>Phone No.</Label>
                      <CharBoxes
                        count={10}
                        value={form.mobileNo}
                        boxSizeMm={5}
                        onChange={(v) => updateForm("mobileNo", v)}
                        printMode={isPrint}
                      />
                    </Box>
                    <Box>
                      <Label>APAAR-ID</Label>
                      <CharBoxes
                        count={12}
                        value={form.apparId}
                        boxSizeMm={5}
                        onChange={(v) => updateForm("apparId", v)}
                        printMode={isPrint}
                      />
                    </Box>
                    <Box>
                      <Label>Pin</Label>
                      <CharBoxes
                        count={6}
                        value={form.pinCode}
                        boxSizeMm={5}
                        onChange={(v) => updateForm("pinCode", v)}
                        printMode={isPrint}
                      />
                    </Box>
                  </Box>
                </Box>

                <Box
                  className="avoid-break"
                  sx={{
                    border: "1px solid #000",
                    p: 1,
                    textAlign: "center",
                    ml: 2,
                    mr: 2,
                    backgroundColor: "white",
                  }}
                >
                  <Typography sx={{ fontSize: "8px", mb: 0.7 }}>
                    परीक्षार्थी का स्वयं का नाम, निगमनांक, डिजिटल फोटो
                  </Typography>
                  <Box
                    sx={{
                      width: "35mm",
                      height: "40mm",
                      border: "1px solid #ccc",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "8px",
                      color: "#666",
                      mb: 0.7,
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    {(() => {
                      const effectivePhotoUrl = studentData?.photoUrl || authenticatedImageUrl;
                      const effectivePhotoLoading = shouldFetchPhoto ? photoLoading : false;
                      const effectivePhotoError = shouldFetchPhoto ? imageError : false;
                      if (effectivePhotoLoading) return <CircularProgress size={20} />;
                      if (effectivePhotoUrl && !effectivePhotoError) {
                        return (
                      <img
                        src={effectivePhotoUrl}
                        alt="Student Photo"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          position: "absolute",
                          top: 0,
                          left: 0,
                        }}
                      />
                        );
                      }
                      return (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          height: "100%",
                          color: "#999",
                        }}
                      >
                        <PersonIcon sx={{ fontSize: "24px", mb: 0.5 }} />
                        <Typography
                          sx={{ fontSize: "8px", textAlign: "center" }}
                        >
                          No Photo
                        </Typography>
                      </Box>
                      );
                    })()}
                  </Box>
                  <Box
                    sx={{
                      width: "35mm",
                      height: "15mm",
                      border: "1px solid #ccc",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "8px",
                      color: "#666",
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    {(() => {
                      const effectiveSigUrl = studentData?.signatureUrl || authenticatedSignatureUrl;
                      const effectiveSigLoading = shouldFetchSignature ? signatureLoading : false;
                      const effectiveSigError = shouldFetchSignature ? signatureError : false;
                      if (effectiveSigLoading) return <CircularProgress size={16} />;
                      if (effectiveSigUrl && !effectiveSigError) {
                        return (
                      <img
                        src={effectiveSigUrl}
                        alt="Student Signature"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          display: "block",
                        }}
                      />
                        );
                      }
                      return (
                      <Typography sx={{ fontSize: "8px", textAlign: "center" }}>
                        Signature
                      </Typography>
                      );
                    })()}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ p: 2, backgroundColor: "white" }}>
              <Typography variant="h6" gutterBottom sx={{ fontSize: "13px" ,fontWeight: "bold"}}>
                9. छात्र का अन्य विवरण:-
              </Typography>

              <Grid container spacing={2} sx={{ fontSize: "12px" }}>
                <Grid item xs={12} sm={4}>
                  <Box border={1} px={2} py={1} height="100%">
                    <Label
                      sx={{
                        fontSize: "14px",
                        fontWeight: "bold",
                        display: "flex",
                        justifyContent: "center",
                        mt: 1,
                      }}
                    >
                      लिंग
                    </Label>
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <Tick
                        label="बालक - 1"
                        checked={form.gender === "1"}
                        onChange={(c) => updateForm("gender", c ? "1" : "")}
                      />
                      <Tick
                        label="बालिका - 2"
                        checked={form.gender === "2"}
                        onChange={(c) => updateForm("gender", c ? "2" : "")}
                      />
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Box border={1} px={2} height="100%">
                    <Typography
                      align="center"
                      sx={{ fontWeight: "bold", mt: 1 }}
                    >
                      जाति
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <Tick
                        label="अनुसूचित जाति - 1"
                        checked={form.caste === "1"}
                        onChange={(c) => updateForm("caste", c ? "1" : "")}
                      />
                      <Tick
                        label="अनुसूचित जनजाति - 2"
                        checked={form.caste === "2"}
                        onChange={(c) => updateForm("caste", c ? "2" : "")}
                      />
                      <Tick
                        label="पिछड़ा वर्ग - 3"
                        checked={form.caste === "3"}
                        onChange={(c) => updateForm("caste", c ? "3" : "")}
                      />
                      <Tick
                        label="सामान्य - 4"
                        checked={form.caste === "4"}
                        onChange={(c) => updateForm("caste", c ? "4" : "")}
                      />
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Typography sx={{ mt: 1, fontSize: "12px" }}>
                    बिन्दु क्र. 11 से 17 तक के प्रकरणों में सक्षम अधिकारी द्वारा
                    सत्यापित प्रमाण-पत्र संलग्न करना आवश्यक है।
                  </Typography>
                </Grid>
              </Grid>

              <Box mb={5} mt={4}>
                <Box sx={{ display: "flex", gap: 4 }}>
                  <Typography
                    sx={{ fontSize: "14px", fontWeight: "Semi-bold" }}
                  >
                    10. संलग्न दस्तावेज
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "12px", fontWeight: "bold", mb: 1 }}
                  >
                    (केवल अर्हकारी परीक्षा अन्य बोर्ड से उत्तीर्ण छात्रों हेतु)
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  sx={{ fontSize: "14px", fontWeight: "Semi-bold" }}
                >
                  (अ) प्रतिलिपि स्थानान्तरण प्रमाण पत्र।
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontSize: "14px", fontWeight: "Semi-bold" }}
                >
                  (ब) अर्हकारी परीक्षा की अंकसूची।
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontSize: "14px", fontWeight: "Semi-bold" }}
                >
                  (स) प्रवर्जन प्रमाण-पत्र।
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box
              className="allow-break"
              p={2}
              sx={{
                backgroundColor: "white",
                borderTop: "1px solid #000",
                borderBottom: "1px solid #000",
              }}
            >
              <Typography
                variant="subtitle1"
                gutterBottom
                sx={{
                  fontSize: "14px",
                  fontWeight: "Bold",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                परीक्षार्थी की घोषणा
              </Typography>
              <Typography
                variant="body2"
                gutterBottom
                sx={{
                  fontSize: "14px",
                  fontWeight: "Semi-bold",
                  whiteSpace: "normal",
                  wordWrap: "break-word",
                  lineHeight: 1.4,
                  textAlign: "justify",
                }}
              >
                मेरे द्वारा दी गई उपरोक्त जानकारी सत्य है यदि इसमें किसी प्रकार
                की असत्य जानकारी पाई गई तो मेरा परीक्षा आवेदन एवं परीक्षा परिणाम
                कभी भी निरस्त किया जा सकता है।
              </Typography>
              <Box display="flex" justifyContent="flex-end">
                <Box textAlign="center">
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "14px", fontWeight: "Bold", mt: 2 }}
                  >
                    परीक्षार्थी के हस्ताक्षर
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box
              className="allow-break"
              p={2}
              sx={{
                backgroundColor: "white",
                borderTop: "1px solid #000",
                borderBottom: "1px solid #000",
                mt: 1,
              }}
            >
              <Typography
                variant="subtitle1"
                gutterBottom
                sx={{
                  fontSize: "14px",
                  fontWeight: "Bold",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                प्राचार्य / अग्रेषण संस्था प्राचार्य का प्रमाणीकरण
              </Typography>
              <Typography
                variant="body2"
                gutterBottom
                sx={{ fontSize: "14px", fontWeight: "Semi-bold" }}
              >
                मैं संबंधित दस्तावेजों की जाँच उपरांत प्रमाणित करता हूँ कि छात्र
                परीक्षा में प्रविष्ट होने हेतु निर्धारित मापदण्ड पूर्ण करता है।
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box>
                    <Typography variant="body2" sx={{ fontSize: "14px" }}>
                      दिनांक:
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 2,
                      mt: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <Box sx={{ display: "flex", gap: 3 }}>
                      <Typography variant="body2" sx={{ fontSize: "14px" }}>
                        संस्था क्रमांक : {form.institutionCode}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontSize: "14px", width: "458px" }}
                      >
                        संस्था का नाम : {form.institutionName}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ fontSize: "14px", fontWeight: "Bold" }}
                      >
                        हस्ताक्षर पदमुद्रा सहित
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box
              className="allow-break"
              px={2}
              py={1}
              sx={{ backgroundColor: "white" }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontStyle: "italic",
                  fontSize: "14px",
                  fontWeight: "Bold",
                }}
              >
                11. CWSN (Children With Special Need):
              </Typography>
              <Box sx={{ ml: 2, mt: 1 }}>
                {[
                  "1. Loco Motor Disability/Leprosy Cured Person, Cerebral Palsy",
                  "2. Visual Impairment, Blindness, Low vision",
                  "3. Hearing Impairment, Deaf/Hard of hearing",
                  "4. Speech & Language Disability",
                  "5. Specific Learning Disabilities",
                  "6. Autism Spectrum Disorder",
                  "7. Chronic Neurological Conditions",
                  "8. Blood Disorder",
                ].map((item) => (
                  <Typography
                    key={item}
                    variant="body2"
                    sx={{ fontSize: "12px", fontWeight: "Bold" }}
                  >
                    {item}
                  </Typography>
                ))}
              </Box>
            </Box>
          </Grid>
        </Grid>

        {!isPrint && (
          <Box sx={{ mt: 2, textAlign: "center" }}>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <Button
                variant="contained"
                color="secondary"
                startIcon={<Image />}
                disabled={isDownloadingImage || isDownloadingPdfFromImage}
                onClick={downloadAsImage}
                sx={{
                  px: 3,
                  py: 1.5,
                  fontSize: 14,
                  fontWeight: 600,
                  minWidth: "200px",
                  backgroundColor: "#9c27b0",
                  "&:hover": { backgroundColor: "#7b1fa2" },
                }}
              >
                {isDownloadingImage
                  ? "Generating Image..."
                  : "Download as Image"}
              </Button>

              <Button
                variant="contained"
                color="primary"
                startIcon={<Download />}
                disabled={isDownloadingImage || isDownloadingPdfFromImage}
                onClick={downloadAsPdfFromImage}
                sx={{
                  px: 3,
                  py: 1.5,
                  fontSize: 14,
                  fontWeight: 600,
                  minWidth: "200px",
                  background:
                    "linear-gradient(135deg, #0168EA 0%, #3790ff 100%)",
                  boxShadow: "0 4px 12px rgba(1, 104, 234, 0.3)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #0056d3 0%, #2f7de8 100%)",
                    boxShadow: "0 6px 16px rgba(1, 104, 234, 0.4)",
                  },
                }}
              >
                {isDownloadingPdfFromImage
                  ? "Generating PDF..."
                  : "Download as PDF"}
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
    </ThemeProvider>
  );
};

export default EleventhExamFormTemplate;